/**
 * DTA meal-planning helpers — provider-neutral.
 * All logic runs client-side; nothing is persisted.
 */

// Scoring: how close a meal fits the user's REMAINING daily target.
// Score 0..100 — closer = higher. Protein weighted highest.
export function scoreMeal(meal, remaining) {
  const safe = (x) => (x > 0 ? x : 1);
  const kcalPenalty = Math.min(1, Math.abs(meal.calories - remaining.calories) / safe(remaining.calories));
  const pPenalty = Math.min(1, Math.abs(meal.protein - remaining.protein) / safe(remaining.protein));
  const cPenalty = Math.min(1, Math.abs(meal.carbs - remaining.carbs) / safe(remaining.carbs));
  const fPenalty = Math.min(1, Math.abs(meal.fat - remaining.fat) / safe(remaining.fat));
  // Weights: protein 0.35, kcal 0.3, carbs 0.2, fat 0.15
  const penalty = pPenalty * 0.35 + kcalPenalty * 0.3 + cPenalty * 0.2 + fPenalty * 0.15;
  return Math.round(Math.max(0, (1 - penalty) * 100));
}

export function matchLabel(score) {
  if (score >= 85) return "Excellent Match";
  if (score >= 70) return "Strong Match";
  if (score >= 55) return "Suitable Match";
  return "Alternative";
}

/**
 * Pick a sensible meal for a given slot based on remaining macros for the day.
 * Attempts to spread the target: breakfast ~25%, lunch ~35%, dinner ~40%.
 */
const SLOT_SHARE = { breakfast: 0.25, lunch: 0.35, dinner: 0.4 };

export function suggestMealForSlot({ slot, dailyTarget, planned, catalogue, excludeIds = [] }) {
  const share = SLOT_SHARE[slot] || 0.33;
  const slotTarget = {
    calories: dailyTarget.calories * share,
    protein: dailyTarget.protein * share,
    carbs: dailyTarget.carbs * share,
    fat: dailyTarget.fat * share,
  };
  const pool = catalogue
    .filter((m) => m.meal_type === slot && !excludeIds.includes(m.id))
    .map((m) => ({ ...m, score: scoreMeal(m, slotTarget) }))
    .sort((a, b) => b.score - a.score);
  return pool[0] || null;
}

export function sumTotals(items) {
  return items.reduce(
    (acc, m) => ({
      calories: acc.calories + (m?.calories || 0),
      protein: acc.protein + (m?.protein || 0),
      carbs: acc.carbs + (m?.carbs || 0),
      fat: acc.fat + (m?.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 },
  );
}

export function remaining(target, planned) {
  return {
    calories: Math.max(0, target.calories - planned.calories),
    protein: Math.max(0, target.protein - planned.protein),
    carbs: Math.max(0, target.carbs - planned.carbs),
    fat: Math.max(0, target.fat - planned.fat),
  };
}

export const TOPUP_FOODS = [
  { id: "greek-yoghurt",       name: "Greek yoghurt (170g)",            calories: 160, protein: 17, carbs: 9,  fat: 5 },
  { id: "high-protein-yoghurt",name: "High-protein yoghurt (170g)",     calories: 120, protein: 20, carbs: 6,  fat: 2 },
  { id: "banana",              name: "Banana",                          calories: 100, protein: 1,  carbs: 25, fat: 0 },
  { id: "apple",               name: "Apple",                           calories: 90,  protein: 1,  carbs: 23, fat: 0 },
  { id: "berries",             name: "Mixed berries (150g)",            calories: 70,  protein: 1,  carbs: 15, fat: 1 },
  { id: "boiled-eggs",         name: "Boiled eggs (×2)",                calories: 155, protein: 13, carbs: 1,  fat: 11 },
  { id: "cottage-cheese",      name: "Cottage cheese (150g)",           calories: 150, protein: 20, carbs: 6,  fat: 5 },
  { id: "protein-shake",       name: "Whey protein shake",              calories: 120, protein: 24, carbs: 3,  fat: 2 },
  { id: "oats",                name: "Porridge oats (50g dry)",         calories: 180, protein: 7,  carbs: 32, fat: 3 },
  { id: "almonds",             name: "Almonds (30g)",                   calories: 175, protein: 6,  carbs: 6,  fat: 15 },
  { id: "wholegrain-toast",    name: "Wholegrain toast (×2)",           calories: 160, protein: 6,  carbs: 28, fat: 2 },
  { id: "mixed-veg",           name: "Steamed mixed veg (200g)",        calories: 60,  protein: 4,  carbs: 10, fat: 0 },
];

// Items that reasonably contribute to a 5-a-day portion when added as a top-up.
const FIVE_A_DAY_IDS = new Set(["banana", "apple", "berries", "mixed-veg"]);

export function portionsFromTopups(topups) {
  // Each counted top-up = 1 portion. Prepared meals are NOT auto-counted per brief.
  return topups.filter((t) => FIVE_A_DAY_IDS.has(t.id)).length;
}
